import { Fragment, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Avatar } from "@/components/shared/Avatar";
import { Button } from "@/components/ui/button";
import { HOST_STATUS_LABEL as STATUS_LABEL, isoWeek, shiftDateKey, weekGrid, type FreeBusyHost, type WeekCell } from "@/lib/meeting-slots";
import { cn } from "@/lib/utils";
import { TONE_PILL } from "@/lib/widget-status";

const WEEKDAYS = ["Man", "Tir", "Ons", "Tor", "Fre"];
const MONTHS = ["jan", "feb", "mar", "apr", "mai", "jun", "jul", "aug", "sep", "okt", "nov", "des"];

const dayNum = (key: string) => Number(key.slice(8));
const monthIdx = (key: string) => Number(key.slice(5, 7)) - 1;
const shortDate = (key: string) => `${dayNum(key)}. ${MONTHS[monthIdx(key)]}`;
const LONG_DAY = new Intl.DateTimeFormat("nb-NO", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Oslo" });
const TIME = new Intl.DateTimeFormat("nb-NO", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Oslo" });

interface BookMeetingWeekCalendarProps {
  hosts: FreeBusyHost[];
  loading: boolean;
  /** Mandagen i uken som vises (Oslo, YYYY-MM-DD). */
  monday: string;
  onMonday: (monday: string) => void;
  /** Tidligste uke som kan vises (uken man kan booke i nå). */
  minMonday: string;
  todayKey: string;
  /** Vert som filter; null er alle verter. */
  filterHostId: string | null;
  onFilterHost: (id: string | null) => void;
  durationMinutes: number;
  holidays: Set<string>;
  notBefore: Date;
  selected: Date | null;
  onSelect: (cell: WeekCell) => void;
}

/**
 * Ukekalenderen i Book møte (VIB-2987): alle verter overlagt eller én vert, mandag til fredag
 * i arbeidstiden. Grønne ruter er ledige, og et klikk velger starttiden. På mobil vises én dag om gangen.
 */
export default function BookMeetingWeekCalendar(p: BookMeetingWeekCalendarProps) {
  const days = useMemo(
    () => weekGrid({ monday: p.monday, hosts: p.hosts, durationMinutes: p.durationMinutes, holidays: p.holidays, hostId: p.filterHostId, notBefore: p.notBefore }),
    [p.monday, p.hosts, p.durationMinutes, p.holidays, p.filterHostId, p.notBefore],
  );
  // Mobil viser én dag: første dag med ledige ruter, til brukeren velger en annen i uken.
  const [pickedDay, setPickedDay] = useState<{ monday: string; index: number } | null>(null);
  const mobileDay = pickedDay?.monday === p.monday ? pickedDay.index : Math.max(0, days.findIndex((d) => d.cells.some((c) => c.state === "free")));
  const selectedMs = p.selected?.getTime() ?? null;
  const spanMs = p.durationMinutes * 60_000;
  const allHosts = p.filterHostId === null;
  const friday = shiftDateKey(p.monday, 4);
  const weekLabel = `Uke ${isoWeek(p.monday)} · ${dayNum(p.monday)}.${monthIdx(p.monday) === monthIdx(friday) ? "" : ` ${MONTHS[monthIdx(p.monday)]}`}–${shortDate(friday)}`;

  const cellText = (cell: WeekCell, prev: WeekCell | undefined) => {
    if (cell.state === "closed") return prev ? "" : "Fridag";
    if (cell.state === "past") return "";
    if (cell.state === "busy") return prev?.state === "busy" ? "" : allHosts ? "Ingen ledige" : "Opptatt";
    if (!allHosts) return "Ledig";
    return `${cell.freeHosts.length} ledig${cell.freeHosts.length > 1 ? "e" : ""}`;
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Vert">
          <HostChip on={allHosts} onClick={() => p.onFilterHost(null)}>Alle verter</HostChip>
          {p.hosts.map((h) => (
            <HostChip key={h.user_id} on={p.filterHostId === h.user_id} disabled={!!h.error} title={h.error ? STATUS_LABEL[h.error] : undefined} onClick={() => p.onFilterHost(h.user_id)} label={h.error ? `${h.full_name}: ${STATUS_LABEL[h.error]}` : h.full_name}>
              <Avatar name={h.full_name} avatarUrl={h.avatar_url} size="sm" className="h-5 w-5 text-[10px]" />
              {h.full_name}
            </HostChip>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" aria-label="Forrige uke" disabled={p.monday <= p.minMonday} onClick={() => p.onMonday(shiftDateKey(p.monday, -7))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[9.5rem] text-center text-sm font-medium tabular-nums" aria-live="polite">{weekLabel}</span>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" aria-label="Neste uke" onClick={() => p.onMonday(shiftDateKey(p.monday, 7))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {p.monday !== p.minMonday && (
            <Button type="button" variant="ghost" size="sm" onClick={() => p.onMonday(p.minMonday)}>
              Denne uken
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 sm:hidden" role="tablist" aria-label="Dag">
        {days.map((d, i) => (
          <button
            key={d.dateKey}
            type="button"
            role="tab"
            aria-selected={mobileDay === i}
            onClick={() => setPickedDay({ monday: p.monday, index: i })}
            className={cn("flex-1 border-[1.5px] py-1 text-center text-xs", mobileDay === i ? "border-foreground bg-foreground text-background" : "border-border")}
          >
            <span className="block text-sm font-medium">{WEEKDAYS[i]}</span>
            {shortDate(d.dateKey)}
            <span className="block text-[10.5px]">{d.closed ? "Fridag" : `${d.cells.filter((c) => c.state === "free").length} ledige`}</span>
          </button>
        ))}
      </div>

      <div
        className="grid max-h-[22rem] grid-cols-[3rem_minmax(0,1fr)] overflow-y-auto border-[1.5px] border-foreground text-xs sm:grid-cols-[3rem_repeat(5,minmax(0,1fr))]"
        aria-label={`Ledighet ${weekLabel}`}
        aria-busy={p.loading}
      >
        <div className="sticky top-0 z-10 border-b-[1.5px] border-foreground bg-background" />
        {days.map((d, i) => (
          <div
            key={d.dateKey}
            className={cn(
              "sticky top-0 z-10 border-b-[1.5px] border-foreground bg-background px-1 py-1.5 text-center font-medium",
              i !== mobileDay && "hidden sm:block",
              d.dateKey === p.todayKey && "text-primary",
            )}
          >
            {WEEKDAYS[i]}
            <span className="block text-[11px] font-normal text-muted-foreground">
              {shortDate(d.dateKey)}
              {d.dateKey === p.todayKey && " · i dag"}
            </span>
          </div>
        ))}
        {days[0].cells.map((row, r) => (
          <Fragment key={row.time}>
            <div className="border-r border-border pr-1.5 text-right text-[11px] leading-[1.875rem] text-muted-foreground tabular-nums">{r % 2 === 0 ? row.time : ""}</div>
            {days.map((d, i) => {
              const cell = d.cells[r];
              const prev = d.cells[r - 1];
              const ms = cell.start.getTime();
              const isStart = selectedMs === ms;
              const inSpan = selectedMs !== null && ms > selectedMs && ms < selectedMs + spanMs;
              const selectable = cell.state === "free" && !p.loading;
              const names = cell.freeHosts.map((h) => h.full_name).join(", ");
              const status = { free: allHosts ? `${cell.freeHosts.length} ledige: ${names}` : "ledig", busy: allHosts ? "ingen ledige" : "opptatt", past: "passert", closed: "fridag" }[cell.state];
              return (
                <button
                  key={d.dateKey}
                  type="button"
                  disabled={!selectable}
                  aria-pressed={isStart}
                  aria-label={`${LONG_DAY.format(cell.start)} ${cell.time}, ${status}`}
                  onClick={() => p.onSelect(cell)}
                  className={cn(
                    "flex h-[1.875rem] min-w-0 items-center justify-between gap-1 border-l border-border px-1.5 text-left",
                    r % 2 === 1 ? "border-b border-border" : "border-b border-border/50",
                    i !== mobileDay && "hidden sm:flex",
                    p.loading && "animate-pulse bg-muted",
                    !p.loading && cell.state === "free" && cn(TONE_PILL.reached, "hover:ring-1 hover:ring-inset hover:ring-current"),
                    !p.loading && cell.state === "busy" && "bg-muted text-muted-foreground",
                    !p.loading && cell.state === "past" && "bg-[repeating-linear-gradient(135deg,hsl(var(--muted))_0_6px,hsl(var(--background))_6px_12px)]",
                    !p.loading && cell.state === "closed" && TONE_PILL.near,
                    isStart && "bg-primary font-medium text-primary-foreground hover:ring-0",
                    inSpan && "bg-primary/70 text-primary-foreground",
                  )}
                >
                  {!p.loading && (isStart ? (
                    <span className="truncate">{cell.time}–{TIME.format(new Date(ms + spanMs))}</span>
                  ) : (
                    <>
                      <span className="truncate">{cellText(cell, prev)}</span>
                      {allHosts && cell.state === "free" && (
                        <span className="hidden -space-x-1 lg:flex" aria-hidden>
                          {cell.freeHosts.slice(0, 3).map((h) => (
                            <Avatar key={h.user_id} name={h.full_name} avatarUrl={h.avatar_url} size="sm" className="h-4 w-4 border border-background text-[8px]" />
                          ))}
                        </span>
                      )}
                    </>
                  ))}
                </button>
              );
            })}
          </Fragment>
        ))}
      </div>
      {p.loading && <p className="text-sm text-muted-foreground" aria-live="polite">Sjekker kalenderne…</p>}
    </div>
  );
}


interface HostChipProps {
  on: boolean;
  disabled?: boolean;
  title?: string;
  label?: string;
  onClick: () => void;
  children: React.ReactNode;
}

function HostChip({ on, disabled, title, label, onClick, children }: HostChipProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      aria-label={label}
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-full border-[1.5px] px-3 text-xs font-medium",
        on ? "border-foreground bg-foreground text-background" : "border-border bg-background hover:bg-muted/50",
        disabled && "cursor-not-allowed border-dashed text-muted-foreground hover:bg-background",
      )}
    >
      {children}
    </button>
  );
}
